import React, { useCallback, useEffect, useMemo, useState } from "react";

import EpcScanner from "../components/EpcScanner";
import LoadingSpinner from "../components/LoadingSpinner";
import SignaturePad from "../components/SignaturePad";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import {
  type ManualCheckinBox,
  type ManualFieldDef,
  type ManualKind,
  type ManualLookupRow,
  type ManualProfile,
  type ManualSubmitResult,
  fetchManualCheckinBoxes,
  fetchManualProfile,
  lookupManualTags,
  submitManualCheckin,
  submitManualCheckout,
} from "../services/manualApi";
import {
  clearCheckouts,
  formatLockoutRemaining,
  msRemainingLockout,
  recordCheckouts,
} from "../utils/recentCheckouts";

/**
 * Manual checkout / checkin page (Jason 2026-06-18; profile-driven 2026-07-26).
 *
 * The captured field set is customer-driven — resolved via
 * `mttr.manual_checkout_profile` off the smartbox's customer. The
 * page fetches the profile once all scanned tags resolve to a single
 * smartbox, then renders whatever fields the profile lists.
 *
 * MC / MI tabs are shown per-profile (mc_enabled / mi_enabled). When
 * only one is enabled, the tab bar hides entirely.
 *
 * Barcode-type fields consume R6 barcode-trigger reads (separate from
 * RFID tag reads, which continue to feed the tag cart).
 */
type Tab = "checkout" | "checkin";

interface ScannedTag extends ManualLookupRow {
  acknowledged?: boolean;
}

export const ManualPage: React.FC = () => {
  const { token, can, role } = useAuth();
  // Vendors are hard-blocked from the manual-checkout page — matches
  // gen5-pwa (companyType=2 short-circuit). Vendors physically don't
  // pull parts from customer boxes; the flow is customer-side. The
  // server routes still respect the capability check; this gate is UX.
  const isVendor = role === "vendor";
  const hasCheckoutCap = !isVendor && can("inventory.manual_checkout");
  const hasCheckinCap = !isVendor && can("inventory.manual_checkin");

  const [tab, setTab] = useState<Tab>(hasCheckoutCap ? "checkout" : "checkin");

  const [tags, setTags] = useState<ScannedTag[]>([]);
  // Manual check-in destination picker (migration 127): the user chooses
  // which visible + checkin-enabled smartbox the tags are being checked back
  // INTO. Checkout keeps deriving its smartbox from the scanned tags.
  const [checkinBoxes, setCheckinBoxes] = useState<ManualCheckinBox[]>([]);
  const [selectedCheckinBox, setSelectedCheckinBox] = useState<number | null>(null);
  const [profile, setProfile] = useState<ManualProfile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<ManualSubmitResult | null>(null);

  const removeTag = useCallback((epc: string): void => {
    setTags((prev) => prev.filter((t) => t.epc !== epc));
  }, []);

  const clearAll = useCallback((): void => {
    setTags([]);
    setLastResult(null);
    setError(null);
    setValues({});
  }, []);

  // ----- Smartbox resolution + profile fetch -----------------------------

  const sharedSmartbox = useMemo(() => {
    const ids = Array.from(
      new Set(tags.map((t) => t.smartbox_id).filter((x): x is number => x != null)),
    );
    return ids.length === 1 ? ids[0] : null;
  }, [tags]);

  const mixedSmartboxes = useMemo(() => {
    const ids = new Set(tags.map((t) => t.smartbox_id).filter((x) => x != null));
    return ids.size > 1;
  }, [tags]);

  // The smartbox a submission targets: for CHECKOUT it's the box the scanned
  // tags resolve to (unchanged); for CHECKIN it's the user-chosen destination.
  const effectiveSmartbox = tab === "checkin" ? selectedCheckinBox : sharedSmartbox;

  // Load the check-in destination list when the checkin tab is active. If
  // exactly one box comes back, pre-select it. Drop a stale selection that
  // is no longer in the list.
  useEffect(() => {
    if (!token || tab !== "checkin") return;
    let cancelled = false;
    fetchManualCheckinBoxes(token)
      .then((r) => {
        if (cancelled) return;
        setCheckinBoxes(r.boxes);
        setSelectedCheckinBox((prev) => {
          if (r.boxes.length === 1) return r.boxes[0].smartbox_id;
          if (prev != null && r.boxes.some((b) => b.smartbox_id === prev)) return prev;
          return null;
        });
      })
      .catch(() => {
        if (cancelled) return;
        setCheckinBoxes([]);
        setSelectedCheckinBox(null);
      });
    return () => { cancelled = true; };
  }, [token, tab]);

  // Fetch profile whenever the resolved smartbox changes. Reset stale
  // field values so a switch from a TXAM box to a Profrac box doesn't
  // leave TXAM values lying around.
  useEffect(() => {
    if (!token || effectiveSmartbox == null) return;
    let cancelled = false;
    setProfileError(null);
    fetchManualProfile(token, effectiveSmartbox)
      .then((p) => {
        if (cancelled) return;
        setProfile(p);
        setValues({});
        // If the currently-selected tab isn't enabled on this profile,
        // flip to the enabled one.
        if (tab === "checkout" && !p.mc_enabled && p.mi_enabled) setTab("checkin");
        if (tab === "checkin" && !p.mi_enabled && p.mc_enabled) setTab("checkout");
      })
      .catch((e) => {
        if (cancelled) return;
        setProfile(null);
        setProfileError(e instanceof Error ? e.message : String(e));
      });
    return () => { cancelled = true; };
    // We only want to refetch on smartbox change / token change, not on
    // tab flips (the tab-flip side-effect above is intentional).
    // `effectiveSmartbox` folds in the checkin destination + tab, so keying
    // on it covers both the checkout (tags) and checkin (picker) paths.
    //
    // The eslint-disable directive that used to sit here named
    // `react-hooks/exhaustive-deps`, but this repo has no eslintConfig at all,
    // so CRA never loads the `react-app` preset that defines that rule — and
    // ESLint treats an unknown rule name in a disable comment as an ERROR.
    // That single line was failing `npm run build` outright (2026-08-14).
    // Removing it is a no-op for linting, since the rule isn't loaded anyway.
    // If the `react-app` preset is ever enabled, restore the directive
    // (written inline here so ESLint doesn't parse this line AS one — a
    // disable comment is recognised even when indented inside prose):
    // >>> eslint-disable-next-line react-hooks/exhaustive-deps <<<
  }, [token, effectiveSmartbox]);

  const activeFields: ManualFieldDef[] = useMemo(() => {
    if (!profile) return [];
    return tab === "checkout" ? profile.mc_fields : profile.mi_fields;
  }, [profile, tab]);

  // ----- Scan routing ---------------------------------------------------
  //
  // Any scanned string — RFID tag, barcode, camera, manual paste —
  // flows through `handleScan`. We decide by FORMAT whether it's an
  // RFID EPC (goes to the tag cart) or a work-order / barcode value
  // (goes into the profile's first barcode-type field). The R6 driver
  // already emits RFID vs. barcode on separate callbacks (accurate at
  // the packet level), but doing the classification here makes the
  // camera + manual paths behave the same and gives one place to slot
  // in the customer-specific work-order format once it's specified.

  const addEpc = useCallback(async (epc: string): Promise<void> => {
    if (!token) return;
    if (tags.some((t) => t.epc === epc)) return;
    // Recent-checkout lockout — refuse an EPC that was checked out in
    // the last 10 min from this browser. Prevents the smartbox3 status
    // propagation race (see utils/recentCheckouts.ts). Only fires on
    // the MC tab; check-ins are exactly what should clear the lockout.
    if (tab === "checkout") {
      const remaining = msRemainingLockout(epc);
      if (remaining > 0) {
        setError(
          `${epc} was checked out here ${formatLockoutRemaining(
            10 * 60 * 1000 - remaining,
          )} ago. Wait ${formatLockoutRemaining(remaining)} or process a check-in first.`,
        );
        return;
      }
    }
    setError(null);
    try {
      const r = await lookupManualTags(token, [epc]);
      const row = r.rfids[0];
      setTags((prev) => prev.concat([row]));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [token, tags, tab]);

  const setBarcodeValue = useCallback((text: string): void => {
    setValues((prev) => {
      const barcodeFields = activeFields.filter((f) => f.type === "barcode");
      if (barcodeFields.length === 0) return prev;
      const empty = barcodeFields.find((f) => !prev[f.key]);
      const target = empty ?? barcodeFields[0];
      return { ...prev, [target.key]: text };
    });
  }, [activeFields]);

  const handleScan = useCallback((raw: string): void => {
    const text = raw.trim();
    if (!text) return;
    const hasBarcodeField = activeFields.some((f) => f.type === "barcode");
    if (hasBarcodeField && !looksLikeRfidEpc(text)) {
      setBarcodeValue(text);
      return;
    }
    void addEpc(text.toUpperCase());
  }, [activeFields, addEpc, setBarcodeValue]);

  const eligibleTags = useMemo(() => {
    if (tab === "checkout") return tags.filter((t) => t.can_checkout);
    return tags.filter((t) => t.can_checkin);
  }, [tab, tags]);

  const ineligibleTags = useMemo(() => {
    if (tab === "checkout") return tags.filter((t) => !t.can_checkout);
    return tags.filter((t) => !t.can_checkin);
  }, [tab, tags]);

  const missingRequired = useMemo(() => {
    return activeFields.filter((f) => {
      if (!f.required) return false;
      const v = values[f.key];
      return v == null || String(v).trim() === "";
    });
  }, [activeFields, values]);

  const kindEnabled = useMemo(() => {
    if (!profile) return true;
    return tab === "checkout" ? profile.mc_enabled : profile.mi_enabled;
  }, [profile, tab]);

  const formReady = useMemo(() => {
    if (effectiveSmartbox == null) return false;
    // Mixed smartboxes only matter for checkout (the target comes from the
    // tags). For checkin the destination is chosen explicitly.
    if (tab === "checkout" && mixedSmartboxes) return false;
    if (!profile || !kindEnabled) return false;
    if (eligibleTags.length === 0) return false;
    if (missingRequired.length > 0) return false;
    return true;
  }, [tab, mixedSmartboxes, effectiveSmartbox, profile, kindEnabled, eligibleTags, missingRequired]);

  const canOperate = tab === "checkout" ? hasCheckoutCap : hasCheckinCap;

  // ----- Submit ----------------------------------------------------------

  const onSubmit = async (): Promise<void> => {
    if (!token || !formReady || effectiveSmartbox == null || !profile) return;
    const rfids = eligibleTags.map((t) => t.epc);
    // Trim + drop empties before sending. The server re-validates.
    const custom: Record<string, string> = {};
    for (const f of activeFields) {
      const raw = values[f.key];
      if (raw == null) continue;
      const s = String(raw).trim();
      if (s !== "") custom[f.key] = s;
    }
    setBusy(true);
    setError(null);
    setLastResult(null);
    try {
      const body = { smartbox_id: effectiveSmartbox, rfids, custom_fields: custom };
      const r = tab === "checkout"
        ? await submitManualCheckout(token, body)
        : await submitManualCheckin(token, body);
      setLastResult(r);
      // Recent-checkout lockout: record the processed EPCs on MC success
      // so a duplicate can't be re-checked-out within the TTL. Clear
      // them on MI success — the tags are back on-hand.
      if (r.processed?.length) {
        if (tab === "checkout") recordCheckouts(r.processed);
        else clearCheckouts(r.processed);
      }
      setTags([]);
      // Preserve non-signature values so an operator doing a second
      // batch at the same location doesn't re-type — signature must
      // always be re-drawn.
      setValues((prev) => {
        const kept: Record<string, string> = {};
        for (const f of activeFields) {
          if (f.type === "signature") continue;
          if (prev[f.key]) kept[f.key] = prev[f.key];
        }
        return kept;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const setFieldValue = (key: string, v: string): void => {
    setValues((prev) => ({ ...prev, [key]: v }));
  };

  // ----- Render ----------------------------------------------------------

  if (isVendor) {
    return (
      <div className="p-6 text-sm text-secondary-600">
        Manual checkout / check-in isn't available to vendor accounts.
        Parts are pulled from customer SmartBoxes by the customer's crew;
        vendors track departures via the Departures page.
      </div>
    );
  }
  if (!hasCheckoutCap && !hasCheckinCap) {
    return (
      <div className="p-6 text-sm text-secondary-600">
        You don't have permission to perform manual checkout or checkin.
      </div>
    );
  }

  const showTabs =
    profile == null
      ? hasCheckoutCap && hasCheckinCap
      : (profile.mc_enabled && hasCheckoutCap) && (profile.mi_enabled && hasCheckinCap);

  return (
    <div className="p-4 md:p-6 max-w-5xl space-y-4">
      <header>
        <h1 className="text-xl font-bold">
          Manual {tab === "checkout" ? "Checkout" : "Checkin"}
        </h1>
        <p className="text-xs text-secondary-500 mt-0.5">
          {tab === "checkout"
            ? "Manually check out RFID tags from a SmartBox. Creates an MC delivery ticket and marks the tags as departed."
            : "Manually check in RFID tags to a SmartBox. Creates an MI ticket; tags that were previously checked out get paired with their MC."}
        </p>
        {profile && (
          <p className="text-[11px] text-secondary-500 mt-1">
            Profile: <span className="font-mono">{profile.profile_key}</span> · {profile.display_name}
          </p>
        )}
      </header>

      {/* Platform operators are exempt from the per-smartbox manual switch
          (migration 127) — say so, so nobody reads an open tab as the
          customer having turned manual on for this box. */}
      {profile?.enablement_bypassed && (
        <div className="text-xs text-secondary-700 bg-secondary-50 border border-secondary-200 px-3 py-2 rounded">
          This SmartBox has manual transactions switched off for its customer.
          You can transact here because you are a platform operator.
        </div>
      )}

      {showTabs && (
        <div className="flex rounded-md border border-secondary-300 overflow-hidden w-fit">
          {hasCheckoutCap && (!profile || profile.mc_enabled) && (
            <button
              type="button" onClick={() => setTab("checkout")}
              className={`px-3 py-1.5 text-sm ${tab === "checkout"
                ? "bg-primary-500 text-white"
                : "bg-white text-secondary-700"}`}
            >
              Checkout (MC)
            </button>
          )}
          {hasCheckinCap && (!profile || profile.mi_enabled) && (
            <button
              type="button" onClick={() => setTab("checkin")}
              className={`px-3 py-1.5 text-sm ${tab === "checkin"
                ? "bg-primary-500 text-white"
                : "bg-white text-secondary-700"}`}
            >
              Checkin (MI)
            </button>
          )}
        </div>
      )}

      {!kindEnabled && profile && (
        <div className="text-sm text-warning-800 bg-warning-50 border border-warning-200 px-3 py-2 rounded">
          {tab === "checkout" ? "Checkout" : "Checkin"} is not enabled for this
          customer (profile <span className="font-mono">{profile.profile_key}</span>).
        </div>
      )}

      {lastResult && (
        <div className={
          "border rounded p-3 text-sm " +
          (lastResult.errors.length > 0
            ? "bg-warning-50 border-warning-200 text-warning-900"
            : "bg-success-50 border-success-200 text-success-800")
        }>
          <div className="font-semibold">
            ✓ Ticket {lastResult.doc_num} created — {lastResult.processed.length} tag
            {lastResult.processed.length === 1 ? "" : "s"} processed.
            {lastResult.errors.length > 0 && <> {lastResult.errors.length} skipped.</>}
          </div>
          {lastResult.warning && (
            <div className="mt-1 text-xs">{lastResult.warning}</div>
          )}
          {lastResult.errors.length > 0 && (
            <ul className="mt-2 text-xs list-disc list-inside space-y-0.5">
              {lastResult.errors.map((e) => (
                <li key={e.epc}><span className="font-mono">{e.epc}</span>: {e.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {profileError && (
        <div className="text-sm text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded">
          Profile fetch failed: {profileError}
        </div>
      )}

      {error && (
        <div className="text-sm text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {mixedSmartboxes && (
        <div className="text-sm text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded">
          ⚠ Tags from multiple smartboxes scanned. A single ticket can only
          cover one smartbox; remove the odd ones or split into two
          submissions.
        </div>
      )}

      {/* Check-in destination picker — the tags are checked back INTO the
          chosen smartbox (must be visible to the user + have check-in
          enabled per migration 127). Pre-selected when exactly one box. */}
      {tab === "checkin" && (
        <div className="bg-white border border-secondary-200 rounded-md p-3 space-y-1">
          <label className="block text-xs text-secondary-600">
            <div className="mb-1 font-medium">
              Check-in destination SmartBox <span className="text-error-600">*</span>
            </div>
            {checkinBoxes.length === 0 ? (
              <div className="text-sm text-warning-800 bg-warning-50 border border-warning-200 px-3 py-2 rounded">
                No SmartBoxes have manual check-in enabled that you can access.
                Ask a platform admin to enable check-in for a box in SmartBox admin.
              </div>
            ) : (
              <select
                className="w-full border border-secondary-300 rounded px-2 py-1.5 text-sm bg-white"
                value={selectedCheckinBox ?? ""}
                onChange={(e) =>
                  setSelectedCheckinBox(e.target.value ? Number(e.target.value) : null)
                }
              >
                <option value="">— Pick a SmartBox —</option>
                {checkinBoxes.map((b) => (
                  <option key={b.smartbox_id} value={b.smartbox_id}>
                    {b.smartbox_code ?? `#${b.smartbox_id}`}
                    {b.scu_name ? ` · ${b.scu_name}` : ""}
                  </option>
                ))}
              </select>
            )}
          </label>
        </div>
      )}

      {/* Scanner — every scan (RFID, barcode, camera, manual) flows through
          handleScan, which routes by FORMAT: RFID-shaped → tag cart,
          anything else → the first barcode-type field on the profile. */}
      <EpcScanner
        onEpc={handleScan}
        onBarcode={handleScan}
      />

      {/* Scanned tags table */}
      <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-secondary-50 text-secondary-700 uppercase">
            <tr>
              <th className="text-left px-3 py-2">EPC</th>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-left px-3 py-2">Smartbox</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Eligibility</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {tags.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-secondary-500">
                Scan a tag to begin.
              </td></tr>
            ) : (
              tags.map((t) => {
                const eligible = tab === "checkout" ? t.can_checkout : t.can_checkin;
                return (
                  <tr key={t.epc} className={eligible ? "" : "bg-warning-50/60"}>
                    <td className="px-3 py-2 font-mono">{t.epc}</td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-secondary-700">{t.sku ?? "—"}</div>
                      {t.item_name && <div className="text-[11px] text-secondary-500">{t.item_name}</div>}
                    </td>
                    <td className="px-3 py-2">{t.smartbox_code ?? "—"}</td>
                    <td className="px-3 py-2 text-secondary-600">
                      {t.lifecycle_status ?? (t.md_status != null ? `md=${t.md_status}` : "—")}
                    </td>
                    <td className="px-3 py-2">
                      {eligible ? (
                        <span className="inline-flex items-center text-[11px] px-1.5 py-0.5 rounded bg-success-100 text-success-700">
                          OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-[11px] px-1.5 py-0.5 rounded bg-warning-100 text-warning-800">
                          {tab === "checkout"
                            ? (t.found ? "not on-hand" : "unknown")
                            : "not eligible"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button" className="text-xs text-secondary-500 hover:text-error-700"
                        onClick={() => removeTag(t.epc)}
                      >
                        remove
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {tags.length > 0 && (
          <div className="px-3 py-2 text-[11px] text-secondary-500 border-t border-secondary-200 flex items-center justify-between">
            <span>
              {eligibleTags.length} of {tags.length} eligible for {tab === "checkout" ? "checkout" : "checkin"}
              {ineligibleTags.length > 0 && <> · {ineligibleTags.length} skipped</>}
            </span>
            <button type="button" className="text-secondary-500 hover:text-error-700" onClick={clearAll}>
              clear all
            </button>
          </div>
        )}
      </div>

      {/* Profile-driven form */}
      {profile && activeFields.length > 0 && (
        <div className="bg-secondary-50 border border-secondary-200 rounded-md p-3 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            {activeFields.map((f) => (
              <ProfileField
                key={f.key}
                def={f}
                value={values[f.key] ?? ""}
                onChange={(v) => setFieldValue(f.key, v)}
              />
            ))}
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            {busy && <LoadingSpinner label="submitting" />}
            <Button
              size="sm" disabled={!formReady || busy || !canOperate}
              onClick={() => void onSubmit()}
            >
              {busy ? "Submitting…" : `Submit ${tab === "checkout" ? "checkout" : "checkin"}`}
            </Button>
          </div>

          {!formReady && tags.length > 0 && !busy && (
            <div className="text-[11px] text-secondary-500">
              {missingRequired.length > 0 && (
                <>Fill: {missingRequired.map((f) => f.label).join(", ")}. </>
              )}
              {eligibleTags.length === 0 && "No eligible tags. "}
              {tab === "checkin" && selectedCheckinBox == null && "Pick a check-in destination SmartBox. "}
              {tab === "checkout" && mixedSmartboxes && "Resolve mixed smartboxes."}
            </div>
          )}
        </div>
      )}

      {profile && activeFields.length === 0 && tags.length > 0 && (
        <div className="bg-secondary-50 border border-secondary-200 rounded-md p-3">
          <div className="flex items-center justify-end gap-2">
            {busy && <LoadingSpinner label="submitting" />}
            <Button
              size="sm" disabled={!formReady || busy || !canOperate}
              onClick={() => void onSubmit()}
            >
              {busy ? "Submitting…" : `Submit ${tab === "checkout" ? "checkout" : "checkin"}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Scan-format detection
// ---------------------------------------------------------------------------

/**
 * A gen5 RFID EPC in practice is uppercase hex, ~12–30 chars (24 is
 * the common length). Anything that's ALL hex within that length range
 * we treat as an RFID read; anything else (e.g. a work-order barcode
 * "WO123456", "PF-1234-5678", …) routes to the barcode field.
 *
 * When a customer's work-order format overlaps with hex (e.g. a
 * 12-char hex WO), the profile can add a customer-specific regex later
 * — for now the classifier is content-only and stateless.
 */
function looksLikeRfidEpc(text: string): boolean {
  const t = text.trim();
  return /^[0-9A-Fa-f]{12,30}$/.test(t);
}

// ---------------------------------------------------------------------------
// Field renderer
// ---------------------------------------------------------------------------

const inputCls = "w-full border border-secondary-300 rounded px-2 py-1.5 text-sm";

// Map column_span (1-6) to the Tailwind classes we ship. Enumerated so
// PurgeCSS can see the exact class strings — dynamic string interpolation
// (`md:col-span-${n}`) gets tree-shaken and ends up rendered without CSS.
const COL_SPAN_CLS: Record<number, string> = {
  1: "md:col-span-1",
  2: "md:col-span-2",
  3: "md:col-span-3",
  4: "md:col-span-4",
  5: "md:col-span-5",
  6: "md:col-span-6",
};

function colSpanClass(def: ManualFieldDef, fallback: number): string {
  const n = def.column_span;
  const use = (typeof n === "number" && n >= 1 && n <= 6) ? n : fallback;
  return COL_SPAN_CLS[use] ?? COL_SPAN_CLS[6];
}

interface ProfileFieldProps {
  def: ManualFieldDef;
  value: string;
  onChange: (v: string) => void;
}

const ProfileField: React.FC<ProfileFieldProps> = ({ def, value, onChange }) => {
  const label = (
    <div className="mb-1">
      {def.label}
      {def.required && <span className="text-error-600"> *</span>}
    </div>
  );

  // Signature + textarea default to full row (6/6). Text/select/barcode
  // default to full row too — a profile can override to 2 (3-per-row)
  // or 3 (2-per-row) or any 1-5 for finer control.
  const span = colSpanClass(def, 6);

  if (def.type === "signature") {
    // NOT a <label>: a label with no htmlFor forwards clicks to its first
    // labelable descendant, which here is SignaturePad's "Clear" button — so
    // every stroke's pointer-up would fire Clear and wipe the signature. A
    // plain <div> keeps the canvas pointer events to itself.
    return (
      <div className={`text-xs text-secondary-600 ${span}`}>
        {label}
        <SignaturePad
          onChange={(d) => onChange(d ?? "")}
          width={480}
          height={160}
        />
      </div>
    );
  }

  if (def.type === "textarea") {
    return (
      <label className={`text-xs text-secondary-600 ${span}`}>
        {label}
        <textarea
          className={inputCls + " min-h-[80px]"}
          value={value}
          maxLength={def.max_length ?? undefined}
          placeholder={def.placeholder ?? undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    );
  }

  if (def.type === "select") {
    return (
      <label className={`text-xs text-secondary-600 ${span}`}>
        {label}
        <select
          className={inputCls}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">—</option>
          {(def.select_options ?? []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </label>
    );
  }

  // text | barcode — same input; barcode-type fields also receive scanner
  // reads via the page-level `onBarcode` handler, but a user can still
  // type into them.
  return (
    <label className={`text-xs text-secondary-600 ${span}`}>
      {label}
      <input
        className={inputCls + (def.type === "barcode" ? " font-mono" : "")}
        value={value}
        maxLength={def.max_length ?? undefined}
        placeholder={def.placeholder ?? undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
};

export default ManualPage;
